import { APP_BASE_HREF } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { ModalComponent } from 'mbs-ui-kit';
import { AwsDetail, Balance, Billing, WasabiDetail } from 'mbs-ui-shared@components/components/models/license.model';
import { BALANCE_TOKEN } from 'mbs-ui-shared@components/licenses/tokens/balance.token';
import { AuthFacade } from 'mbs-ui-shared@facades/auth.facade';
import { LicenseMode } from 'mbs-ui-shared@models/ProviderData';
import { LicensesService } from 'mbs-ui-shared@services';
import { Observable, catchError, finalize, of } from 'rxjs';

@Component({
  selector: 'mbs-renew-license-modal',
  templateUrl: './renew-license-modal.component.html',
  styles: ['.hint { position: absolute; top: 3px; margin-left: 5px;  }']
})
export class RenewLicenseModalComponent implements OnInit {
  public loading = true;
  public renewLoading = false;

  public totalPrice: number;
  public licensesInfo: Billing;
  public awsStoragesInfo: Array<AwsDetail>;
  public wasabiStoragesInfo: Array<WasabiDetail>;

  @ViewChild(ModalComponent, { static: true }) baseModal: ModalComponent;

  public get showMinCommitment() {
    return Number(this.total) <= 30;
  }

  public get isPaymentLessThanOneDollar(): boolean {
    return this.discountedPrice <= 1;
  }

  public get isNotEnoughCreditsToPay(): boolean {
    return this.discountedPrice > this.balanceValue;
  }

  public balanceValue: number;

  public get awsStoragesTotalCost(): number {
    return this.awsStoragesInfo?.reduce((acc, storage) => acc + storage.totalCost, 0) || 0;
  }

  public get wasabiStoragesTotalCost(): number {
    return this.wasabiStoragesInfo?.reduce((acc, storage) => acc + storage.total, 0) || 0;
  }

  public get storagesTotalCost(): number {
    return this.awsStoragesTotalCost + this.wasabiStoragesTotalCost;
  }

  public get totalAmount(): number {
    return this.storagesTotalCost + this.discountedPrice;
  }

  public get isYearlyLicenseMode() {
    const licenseMode = this.auth.currentUserValue.ProviderInfo.LicenseMode;
    return (
      licenseMode === LicenseMode.Yearly ||
      licenseMode === LicenseMode.CumulativeV2Annual ||
      licenseMode === LicenseMode.CumulativeV3Annual ||
      licenseMode === LicenseMode.CumulativeV4Annual
    );
  }

  public get totalLicenses(): number {
    return this.licensesInfo?.licenses?.reduce((acc, license) => acc + license.quantity, 0);
  }

  public get totalStorages(): number {
    return (this.wasabiStoragesInfo?.reduce((acc, storage) => acc + storage.quantity, 0) || 0) + (this.awsStoragesInfo?.length || 0);
  }

  public get discountedPrice(): number {
    return this.licensesInfo?.proratedPrice - this.licensesInfo?.discount || 0;
  }

  public get total(): number {
    return this.isNotEnoughCreditsToPay ? this.totalPrice - this.balanceValue : 0;
  }

  public get minCommitment(): number {
    return this.baseModal.data?.minCommitment;
  }

  constructor(
    private licensesService: LicensesService,
    private auth: AuthFacade,
    @Inject(BALANCE_TOKEN) public balance: Observable<Balance>,
    @Inject(APP_BASE_HREF) private appBaseHref: string
  ) {}

  ngOnInit(): void {
    this.licensesService
      .getRenew(this.baseModal.data?.params)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe((billing) => {
        this.balanceValue = billing.credits;
        this.totalPrice = billing.total;
        this.licensesInfo = billing?.licensesInfo;
        this.awsStoragesInfo = billing?.storagesInfo?.details;
        this.wasabiStoragesInfo = billing?.storagesInfo?.wasabiDetails;
      });
  }

  public renew() {
    this.renewLoading = true;

    this.licensesService
      .renew(this.baseModal.data?.params)
      .pipe(
        finalize(() => (this.renewLoading = false)),
        catchError(() => of(null))
      )
      .subscribe((response) => {
        if (response === null) {
          return;
        }

        const url = response;
        const isEnoughCreditsToPay = url === '';

        if (isEnoughCreditsToPay) {
          this.baseModal.save();
          return;
        }

        location.href = String(url).replace('~', '').replaceAll('"', '');
      });
  }

  public getTotalDiff(): number {
    return Math.abs(this.discountedPrice - this.balanceValue);
  }
}
